import React, { useEffect, useState } from "react";
import CommonApiService from "../../ApiServices/CommonApiService";
import Sidebar from "../Side/Sidebar";
import Topbar from "../Side/Topbar";
import { useAuth } from "../Auth/AuthContext";
import AdminSidebar from "../Side/AdminSidebar";
import { Hash, FileText, AlignLeft, CheckCircle } from "lucide-react";

export default function Notes() {
    const { authState } = useAuth();
    const role = authState.roles;
    const isAdmin = role === "Admin";

    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newNote, setNewNote] = useState({ title: "", description: "" });

    useEffect(() => {
        fetchNotes();
    }, []);

    const fetchNotes = async () => {
        try {
            const data = await CommonApiService.getAllNotes();
            setNotes(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.title || !newNote.description) return;
        try {
            await CommonApiService.createNote(newNote);
            setIsModalOpen(false);
            setNewNote({ title: "", description: "" });
            fetchNotes(); // Refresh list after adding
        } catch (error) {
            console.error("Error adding note:", error);
        }
    };
    const headers = [
        { label: "ID", icon: <Hash size={16} /> },
        { label: "Title", icon: <FileText size={16} /> },
        { label: "Description", icon: <AlignLeft size={16} /> },
        { label: "Status", icon: <CheckCircle size={16} /> }
    ];


    return (
        <div className="min-h-screen flex flex-col md:flex-row">
            {/* Sidebar (Admin or User) */}
            {isAdmin ? <AdminSidebar /> : <Sidebar />}

            {/* Main Content */}
            <div className="flex-1 bg-gray-100">
                <Topbar />

                <main className="mt-20 pt-6 px-6">
                    {/* Add Note Button */}
                    <button
                        className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        onClick={() => setIsModalOpen(true)}
                    >
                        + Add Note
                    </button>

                    {/* Notes Table */}
                    <div className="overflow-x-auto bg-white p-4 shadow-lg rounded-lg">
                        {loading ? (
                            <p>Loading notes...</p>
                        ) : error ? (
                            <p className="text-red-500">Error: {error}</p>
                        ) : (
                            <table className="min-w-full bg-white border border-gray-300 shadow-md rounded-lg">
                                <thead className="bg-gradient-to-r from-purple-600 to-pink-500 text-white">
                                    <tr>
                                        {headers.map(({ label, icon }) => (
                                            <th key={label} className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider">
                                                <div className="flex items-center gap-2">
                                                    {icon} {label}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {notes.length > 0 ? (
                                        notes.map((note) => (
                                            <tr key={note.id} className="text-center bg-white hover:bg-gray-100">
                                                <td className="border p-3">{note.id}</td>
                                                <td className="border p-3">{note.title}</td>
                                                <td className="border p-3">{note.description}</td>
                                                <td className="border p-3">{note.isActive ? "Active" : "Inactive"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center p-4">
                                                No notes available.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Modal for Adding a Note */}
                    {isModalOpen && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg w-1/3">
                                <h3 className="text-xl font-bold mb-4">Add New Note</h3>
                                <input
                                    type="text"
                                    placeholder="Title"
                                    className="w-full p-2 border border-gray-300 rounded mb-3"
                                    value={newNote.title}
                                    onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                                />
                                <textarea
                                    placeholder="Description"
                                    className="w-full p-2 border border-gray-300 rounded mb-3"
                                    value={newNote.description}
                                    onChange={(e) => setNewNote({ ...newNote, description: e.target.value })}
                                />
                                <div className="flex justify-end">
                                    <button
                                        className="px-4 py-2 bg-gray-400 text-white rounded mr-2"
                                        onClick={() => setIsModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                                        onClick={handleAddNote}
                                    >
                                        Save Note
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
